import React, { useState, useEffect, useRef } from 'react';
import { PaperAirplaneIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useP2PChat } from '../../hooks/useP2P';
import { formatDistanceToNow } from 'date-fns';

const P2PChat = ({ orderId }) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  const { messages, loading, sendMessage } = useP2PChat(orderId);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await sendMessage({
        message_type: 'text',
        content: newMessage.trim()
      });
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 p-5 bg-gradient-to-b from-gray-800 to-gray-850 rounded-xl mb-5 border border-gray-700 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-white py-12">
              <ChatBubbleLeftRightIcon className="h-16 w-16 mx-auto mb-4 text-white" />
              <p className="text-lg font-medium mb-2">No messages yet</p>
              <p className="text-sm text-white">Start the conversation with your trading partner</p>
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.sender_profile ? 'justify-start' : 'justify-end'
              }`}
            >
              <div
                className={`max-w-[80%] px-5 py-3.5 rounded-2xl shadow-md ${
                  message.message_type === 'system'
                    ? 'bg-gray-700/50 text-white text-center mx-auto border border-gray-600'
                    : message.sender_profile
                      ? 'bg-gradient-to-br from-gray-700 to-gray-750 text-white border border-gray-600'
                      : 'bg-gradient-to-br from-blue-600 to-blue-700 text-white'
                }`}
              >
                {message.message_type === 'system' ? (
                  <div className="text-center text-sm text-white italic">
                    {message.content}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm font-bold">
                        {message.sender_profile?.nickname || 'You'}
                      </span>
                      <span className="text-xs opacity-70">
                        {message.created && !isNaN(new Date(message.created).getTime()) 
                          ? formatDistanceToNow(new Date(message.created), { addSuffix: true })
                          : 'Just now'
                        }
                      </span>
                    </div>
                    <p className="text-base break-words leading-relaxed">{message.content}</p>
                  </>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage}>
        <div className="flex space-x-3 bg-gray-800 p-3 rounded-xl border border-gray-700">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            disabled={sending}
            className="flex-1 bg-gray-750 border border-gray-600 rounded-xl px-5 py-3.5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base transition-all"
          />
          <button
            type="submit"
            disabled={sending || !newMessage.trim()}
            className="px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center min-w-[60px] font-medium shadow-lg"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <div className="flex items-center space-x-2">
                <PaperAirplaneIcon className="h-5 w-5" />
                <span className="hidden sm:inline">Send</span>
              </div>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default P2PChat;
